// @ts-nocheck
(() => {
  if (
    customElements.get('sheet-trigger') ||
    customElements.get('sheet-content')
  ) {
    return;
  }

  const sheets = new Map();
  const openSheets = [];

  const lockState = {
    count: 0,
    scrollY: 0,
    previousHtmlOverflow: '',
    previousBodyOverflow: '',
    previousBodyPosition: '',
    previousBodyTop: '',
    previousBodyWidth: '',
    previousPaddingRight: '',
    scrollbarWidth: 0,
  };

  const lockBody = () => {
    lockState.count += 1;

    if (lockState.count === 1) {
      lockState.scrollY = window.scrollY;
      lockState.previousHtmlOverflow = document.documentElement.style.overflow;
      lockState.previousBodyOverflow = document.body.style.overflow;
      lockState.previousBodyPosition = document.body.style.position;
      lockState.previousBodyTop = document.body.style.top;
      lockState.previousBodyWidth = document.body.style.width;
      lockState.previousPaddingRight = document.body.style.paddingRight;
      lockState.scrollbarWidth = Math.max(
        0,
        window.innerWidth - document.documentElement.clientWidth
      );

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      if (lockState.scrollbarWidth > 0) {
        document.body.style.paddingRight = `${lockState.scrollbarWidth}px`;
      }

      document.body.style.position = 'fixed';
      document.body.style.top = `-${lockState.scrollY}px`;
      document.body.style.width = '100%';
    }
  };

  const unlockBody = () => {
    lockState.count = Math.max(0, lockState.count - 1);

    if (lockState.count === 0) {
      const scrollY = lockState.scrollY;

      document.documentElement.style.overflow = lockState.previousHtmlOverflow;
      document.body.style.overflow = lockState.previousBodyOverflow;
      document.body.style.position = lockState.previousBodyPosition;
      document.body.style.top = lockState.previousBodyTop;
      document.body.style.width = lockState.previousBodyWidth;
      document.body.style.paddingRight = lockState.previousPaddingRight;

      lockState.scrollY = 0;
      lockState.previousHtmlOverflow = '';
      lockState.previousBodyOverflow = '';
      lockState.previousBodyPosition = '';
      lockState.previousBodyTop = '';
      lockState.previousBodyWidth = '';
      lockState.previousPaddingRight = '';
      lockState.scrollbarWidth = 0;

      window.scrollTo(0, scrollY);
    }
  };

  const parseTransitionTimeMs = (value) => {
    if (!value) {
      return 0;
    }

    const normalized = value.trim().toLowerCase();
    if (normalized.endsWith('ms')) {
      return Number.parseFloat(normalized) || 0;
    }

    if (normalized.endsWith('s')) {
      return (Number.parseFloat(normalized) || 0) * 1000;
    }

    return 0;
  };

  const getMaxTransitionTimeMs = (element) => {
    if (!element) {
      return 0;
    }

    const styles = window.getComputedStyle(element);
    const durations = styles.transitionDuration
      .split(',')
      .map((value) => parseTransitionTimeMs(value));
    const delays = styles.transitionDelay
      .split(',')
      .map((value) => parseTransitionTimeMs(value));
    const pairCount = Math.max(durations.length, delays.length);
    let max = 0;

    for (let index = 0; index < pairCount; index += 1) {
      const duration = durations[index] ?? durations[durations.length - 1] ?? 0;
      const delay = delays[index] ?? delays[delays.length - 1] ?? 0;
      max = Math.max(max, duration + delay);
    }

    return max;
  };

  const getTopOpenSheet = () => openSheets[openSheets.length - 1] || null;

  const removeOpenSheet = (sheet) => {
    const index = openSheets.indexOf(sheet);
    if (index >= 0) {
      openSheets.splice(index, 1);
    }
  };

  const SHEET_STATE_CLASSES = new Set(['sheet', 'is-open', 'is-closing']);

  /**
   * @param {HTMLElement} existing
   * @param {HTMLElement} fresh
   */
  const syncColorSchemeClasses = (existing, fresh) => {
    existing.classList.forEach((className) => {
      if (className.startsWith('color-scheme--')) {
        existing.classList.remove(className);
      }
    });

    fresh.classList.forEach((className) => {
      if (className.startsWith('color-scheme--')) {
        existing.classList.add(className);
      }
    });
  };

  /**
   * @param {HTMLElement} existing
   * @param {HTMLElement} fresh
   */
  const syncSheetClasses = (existing, fresh) => {
    syncColorSchemeClasses(existing, fresh);

    existing.classList.forEach((className) => {
      if (
        SHEET_STATE_CLASSES.has(className) ||
        className.startsWith('color-scheme--')
      ) {
        return;
      }

      if (!fresh.classList.contains(className)) {
        existing.classList.remove(className);
      }
    });

    fresh.classList.forEach((className) => {
      if (
        SHEET_STATE_CLASSES.has(className) ||
        className.startsWith('color-scheme--')
      ) {
        return;
      }

      existing.classList.add(className);
    });
  };

  /**
   * @param {InstanceType<typeof SheetContent>} existing
   * @param {InstanceType<typeof SheetContent>} fresh
   */
  const syncSheetFromClone = (existing, fresh) => {
    const freshStyle = fresh.getAttribute('style');
    if (freshStyle) {
      existing.setAttribute('style', freshStyle);
    } else {
      existing.removeAttribute('style');
    }

    if (fresh.dataset.sheetWidth) {
      existing.dataset.sheetWidth = fresh.dataset.sheetWidth;
    }

    if (fresh.dataset.direction) {
      existing.dataset.direction = fresh.dataset.direction;
    }

    syncSheetClasses(existing, fresh);

    const existingPanel =
      existing.panelElement || existing.querySelector('.sheet__panel');
    const freshPanel =
      fresh.panelElement || fresh.querySelector('.sheet__panel');

    if (existingPanel && freshPanel) {
      existingPanel.innerHTML = freshPanel.innerHTML;
    }

    existing.applySettings();
  };

  /**
   * @param {string} sheetId
   * @param {HTMLElement} liveSheet
   */
  const removeOrphanSheets = (sheetId, liveSheet) => {
    document
      .querySelectorAll(`sheet-content#${CSS.escape(sheetId)}`)
      .forEach((sheet) => {
        if (sheet !== liveSheet) {
          sheet.remove();
        }
      });
  };

  /**
   * @param {string} sheetId
   * @param {HTMLElement} current
   * @returns {HTMLElement | null}
   */
  const getExistingSheetById = (sheetId, current) => {
    const registered = sheets.get(sheetId);
    if (registered && registered !== current) {
      return registered;
    }

    const found = document.querySelector(
      `sheet-content#${CSS.escape(sheetId)}`
    );
    if (found && found !== current) {
      return found;
    }

    return null;
  };

  class SheetTrigger extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.updateAria();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
    }

    static get observedAttributes() {
      return ['id', 'data-sheet-target'];
    }

    attributeChangedCallback() {
      this.updateAria();
    }

    getTargetId() {
      return (
        this.dataset.sheetTarget ||
        this.getAttribute('aria-controls') ||
        this.getAttribute('id')
      );
    }

    updateAria() {
      const targetId = this.getTargetId();
      if (targetId) {
        this.setAttribute('aria-controls', targetId);
      }
    }

    onClick = (event) => {
      const targetId = this.getTargetId();
      if (!targetId) {
        return;
      }

      const interactiveElement = event.target.closest(
        'button, a, [role="button"], input, select, textarea, label'
      );
      if (!interactiveElement && event.target === this) {
        return;
      }

      event.preventDefault();
      window.sheet?.toggle(targetId);
    };

    onKeyDown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      const targetId = this.getTargetId();
      if (!targetId) {
        return;
      }

      event.preventDefault();
      window.sheet?.toggle(targetId);
    };
  }

  class SheetContent extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.initialized = false;
      this.restoreFocusElement = null;
      this.overlayElement = null;
      this.panelElement = null;
      this.defaultCloseButton = null;
      this.closeAnimationCleanup = null;
      this.backdropCursor = null;
      this.eventAbortController = null;
    }

    connectedCallback() {
      this.initialize();

      if (this.parentElement !== document.body) {
        const existing =
          this.id && this.id.length > 0
            ? getExistingSheetById(this.id, this)
            : null;

        if (existing) {
          syncSheetFromClone(existing, this);
          removeOrphanSheets(this.id, existing);
          this.remove();
          return;
        }

        document.body.appendChild(this);
        return;
      }

      this.bindEvents();
      this.ensureBackdropCursor();
      sheets.set(this.id, this);
      this.applySettings();
    }

    disconnectedCallback() {
      this.unbindEvents();

      if (sheets.get(this.id) === this) {
        sheets.delete(this.id);
      }

      if (this.isOpen || this.hasAttribute('scroll-lock')) {
        if (this.closeAnimationCleanup) {
          this.closeAnimationCleanup({ skipUnlock: true });
        }

        removeOpenSheet(this);
        this.removeAttribute('scroll-lock');
        unlockBody();
        this.isOpen = false;
      }

      this.backdropCursor?.destroy();
      this.backdropCursor = null;
    }

    static get observedAttributes() {
      return ['id', 'data-sheet-width', 'data-direction'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'id' && oldValue && sheets.get(oldValue) === this) {
        sheets.delete(oldValue);
      }

      if (name === 'id' && newValue) {
        sheets.set(newValue, this);
      }

      if (!this.initialized) {
        return;
      }

      this.applySettings();
    }

    initialize() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.classList.add('sheet');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.setAttribute('aria-hidden', 'true');

      const existingNodes = Array.from(this.childNodes);

      const overlay = document.createElement('button');
      overlay.type = 'button';
      overlay.className = 'sheet__overlay';
      overlay.setAttribute('aria-label', 'Close sheet');

      const panel = document.createElement('div');
      panel.className = 'sheet__panel';

      existingNodes.forEach((node) => panel.appendChild(node));

      const existingCloseButton = panel.querySelector('[data-sheet-close]');
      if (!existingCloseButton) {
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className =
          'sheet__close button button-size-icon-md button-variant-primary';
        closeButton.setAttribute('aria-label', 'Close sheet');
        closeButton.setAttribute('data-sheet-close', '');
        closeButton.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-close" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
        panel.prepend(closeButton);
        this.defaultCloseButton = closeButton;
      }

      this.appendChild(overlay);
      this.appendChild(panel);

      this.overlayElement = overlay;
      this.panelElement = panel;
    }

    ensureBackdropCursor() {
      if (
        !this.overlayElement ||
        typeof window.initBackdropCursor !== 'function'
      ) {
        return;
      }

      this.backdropCursor?.destroy();
      this.backdropCursor = window.initBackdropCursor({
        root: this.overlayElement,
        isEnabled: () => this.isOpen,
      });
    }

    bindEvents() {
      if (!this.overlayElement) {
        return;
      }

      this.unbindEvents();
      this.eventAbortController = new AbortController();
      const { signal } = this.eventAbortController;

      this.addEventListener('click', this.handleClick, { signal });
      this.overlayElement.addEventListener('click', this.close, { signal });
    }

    unbindEvents() {
      this.eventAbortController?.abort();
      this.eventAbortController = null;
    }

    applySettings() {
      const sheetWidth = this.dataset.sheetWidth || '420px';
      const direction = this.dataset.direction || 'right';
      const allowedDirections = ['left', 'right', 'top', 'bottom'];
      const normalizedDirection = allowedDirections.includes(direction)
        ? direction
        : 'right';

      this.style.setProperty('--sheet-width', sheetWidth);

      if (this.dataset.direction !== normalizedDirection) {
        this.setAttribute('data-direction', normalizedDirection);
      }
    }

    open = () => {
      if (this.isOpen) {
        return;
      }

      const wasClosing = this.classList.contains('is-closing');

      if (this.closeAnimationCleanup) {
        this.closeAnimationCleanup({ skipUnlock: true });
      }

      this.isOpen = true;
      this.restoreFocusElement = document.activeElement;
      this.setAttribute('aria-hidden', 'false');
      this.classList.remove('is-closing');
      this.classList.add('is-open');

      if (!wasClosing) {
        lockBody();
        this.setAttribute('scroll-lock', '');
      }

      removeOpenSheet(this);
      openSheets.push(this);

      requestAnimationFrame(() => {
        const focusTarget = this.querySelector(
          '[data-sheet-close], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        focusTarget?.focus();
      });

      this.dispatchEvent(new CustomEvent('sheet:open', { bubbles: true }));
    };

    close = () => {
      if (!this.isOpen || this.classList.contains('is-closing')) {
        return;
      }

      this.isOpen = false;
      this.setAttribute('aria-hidden', 'true');
      this.classList.add('is-closing');
      this.classList.remove('is-open');
      this.backdropCursor?.hide();
      removeOpenSheet(this);

      const finishClose = ({ skipUnlock = false } = {}) => {
        this.classList.remove('is-closing');
        this.closeAnimationCleanup = null;

        if (!skipUnlock && !this.isOpen) {
          this.removeAttribute('scroll-lock');
          unlockBody();
        }
      };

      const onTransitionEnd = (event) => {
        if (
          event.target !== this.panelElement ||
          event.propertyName !== 'transform'
        ) {
          return;
        }

        if (this.closeAnimationCleanup) {
          this.closeAnimationCleanup();
        }
      };

      const transitionMs = getMaxTransitionTimeMs(this.panelElement);
      const fallbackTimeout = window.setTimeout(
        () => {
          if (this.closeAnimationCleanup) {
            this.closeAnimationCleanup();
          }
        },
        Math.max(transitionMs + 80, 320)
      );

      this.panelElement?.addEventListener('transitionend', onTransitionEnd);

      this.closeAnimationCleanup = (options = {}) => {
        window.clearTimeout(fallbackTimeout);
        this.panelElement?.removeEventListener(
          'transitionend',
          onTransitionEnd
        );
        finishClose(options);
      };

      if (this.restoreFocusElement instanceof HTMLElement) {
        this.restoreFocusElement.focus({ preventScroll: true });
      }

      this.dispatchEvent(new CustomEvent('sheet:close', { bubbles: true }));
    };

    toggle = () => {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    };

    handleClick = (event) => {
      const closeButton = event.target.closest('[data-sheet-close]');
      if (closeButton) {
        event.preventDefault();
        this.close();
      }
    };
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    const topSheet = getTopOpenSheet();
    if (topSheet) {
      topSheet.close();
    }
  });

  customElements.define('sheet-trigger', SheetTrigger);
  customElements.define('sheet-content', SheetContent);

  window.sheet = {
    open(id) {
      sheets.get(id)?.open();
    },
    close(id) {
      sheets.get(id)?.close();
    },
    toggle(id) {
      sheets.get(id)?.toggle();
    },
  };
})();
